import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import * as React from "react"
import { ConfigSectionNav } from "@/components/config-page/ConfigSectionNav"
import { PromotionFormShell } from "@/components/promotions/promotion-form-shell"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { ClientVerificationEditor } from "@/features/client-verification/ClientVerificationEditor"
import {
	CLIENT_VERIFICATION_ANCHOR_PREFIX,
	CLIENT_VERIFICATION_SECTIONS,
} from "@/features/client-verification/client-verification-sections"
import {
	PAGE_DESCRIPTION,
	PAGE_TITLE,
} from "@/features/client-verification/copy"
import { useClientVerificationSettings } from "@/queries/marketplace"

export const Route = createFileRoute("/configuration/client-verification")({
	component: ClientVerificationPage,
})

/** The page layout with skeleton sections, or only the load error once loading has failed. */
function LoadingShell({ error }: { error: Error | null }) {
	return (
		<PromotionFormShell
			backLink={null}
			title={PAGE_TITLE}
			description={PAGE_DESCRIPTION}
			submitError={
				error ? `Failed to load the settings: ${error.message}` : null
			}
			summary={
				error ? null : (
					<>
						<ConfigSectionNav
							sections={CLIENT_VERIFICATION_SECTIONS}
							anchorPrefix={CLIENT_VERIFICATION_ANCHOR_PREFIX}
							className="hidden xl:block"
						/>
						<Skeleton className="h-72 w-full rounded-lg" />
					</>
				)
			}
		>
			{error ? null : (
				<>
					<Skeleton className="h-64 w-full rounded-lg" />
					<Skeleton className="h-64 w-full rounded-lg" />
					<Skeleton className="h-64 w-full rounded-lg" />
				</>
			)}
		</PromotionFormShell>
	)
}

function ClientVerificationPage() {
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canRead = hasRole(AdminRole.Superadmin)
	const { data: settings, error } = useClientVerificationSettings()

	React.useEffect(() => {
		if (!canRead) navigate({ to: "/unauthorized" })
	}, [canRead, navigate])
	if (!canRead) return null

	if (!settings) {
		return <LoadingShell error={error} />
	}

	return <ClientVerificationEditor settings={settings} />
}
